// Started is stored in the database as "Confirmed"

/**
 * Meeting statuses
 */
export enum MeetingStatus {
  Scheduled = "Scheduled",
  Started = "Confirmed",
  Cancelled = "Cancelled",
  Training = "Training",
  Proposed = "Proposed",
  Abandoned = "Abandoned",
  Ended = "Ended",
}

/**
 * Meeting join types
 */
export enum MeetingJoinType {
  Web = "Web",
  Headset = "Headset",
  Phone = "Phone",
}

/**
 * Meeting layouts
 */
export type MeetingLayout = "metro" | "remote" | "training";

/**
 * Active status for users, organisations, and patients
 */
export enum ActiveStatus {
  Inactive = "Inactive",
  Active = "Active"
}

/**
 * Meeting views
 */
export enum MeetingView {
  Remote = "Remote",
  Metro = "Metro",
}

/**
 * Resource types
 */
export enum ResourceType {
  User = "User",
  Organisation = "Organisation",
  Patient = "Patient",
  Meeting = "Meeting",
  Chime = "Chime",
  Client = "Client",
}

/**
 * Client types
 */
export enum ClientType {
  Active = "Active",
  Inactive = "Inactive",
  Banned = "Banned",
}

/**
 * User types
 */
export enum UserType {
  Blocked = "Blocked",
  Inactive = "Inactive",
  Guest = "Guest",
  User = "User",
  OrgAdmin = "Organisation Admin",
  SysAdmin = "System Admin",
  DeveloperAdmin = "Developer Admin"
}

/**
 * Email preferences for users
 */
export enum EmailPreferences {
  All = "All",
  OnlyJoin = "OnlyJoin",
}

/**
 * User interface
 * @param userId - The unique identifier for the user
 * @param email - The email address of the user
 * @param userType - The type of the user
 * @param organisationIds - The IDs of organisations the user belongs to
 * @param adminIds - The IDs of organisations the user administers
 * @param fullName - The full name of the user
 * @param jobTitle - The job title of the user
 * @param lastLogin - The last login date of the user
 * @param emailPreferences - The email preferences of the user
 * @param clientId - The ID of the client's primary association
 * @param allClientIds - The IDs of all clients the user is associated with
 */
export interface User {
  userId: string
  email: string
  userType: UserType
  organisationIds: string[]
  adminIds: string[]
  fullName: string
  jobTitle: string
  lastLogin?: Date
  emailPreferences?: EmailPreferences
  clientId: string
  allClientIds: string[]
}

/**
 * Client data interface
 * @param clientId - The unique identifier for the client
 * @param organisationIds - The IDs of organisations the client is associated with
 * @param adminIds - The IDs of organisations the client administers
 * @param userType - The type of the user within the client
 */
export interface ClientData {
  clientId: string
  organisationIds: string[]
  adminIds: string[],
  userType: UserType
}

/**
 * Client user interface
 * @param userId - The unique identifier for the user
 * @param email - The email address of the user
 * @param fullName - The full name of the user
 * @param jobTitle - The job title of the user
 * @param clientIds - The IDs of clients the user is associated with
 * @param clientData - The client data associated with the user
 * @param lastLogin - The last login date of the user
 * @param emailPreferences - The email preferences of the user
 */
export interface ClientUser {
  userId: string
  email: string
  fullName: string
  jobTitle: string
  clientIds: string[]
  clientData: ClientData[]
  lastLogin?: Date
  emailPreferences?: EmailPreferences
}

/**
 * Client interface
 * @param clientId - The unique identifier for the client
 * @param clientName - The name of the client
 * @param organisationIds - The IDs of organisations the client is associated with
 * @param domainWhiteList - The list of allowed email domains meeting substitution rules
 * @param type - The type of the client
 * @param created - The creation date of the client
 */
export interface Client {
  clientId: string
  clientName: string
  organisationIds: string[]
  domainWhiteList: string[]
  type: ClientType
  created: Date
}

/**
 * Organisation interface -> Displayed as "Team" on Frontend
 * @param organisationId - The unique identifier for the organisation
 * @param name - The name of the organisation
 * @param type - The active status of the organisation
 * @param meetingViews - The views of meetings associated with the organisation
 * @param color - The color associated with the organisation
 * @param usersIds - The IDs of users belonging to the organisation
 * @param adminIds - The IDs of users administering the organisation
 * @param created - The creation date of the organisation
 * @param clientIds - The IDs of clients the organisation is associated with
 */
export interface Organisation {
  organisationId: string
  name: string
  type: ActiveStatus
  meetingViews?: MeetingView[] | undefined
  color: string
  usersIds: string[]
  adminIds: string[]
  created: Date
  clientIds?: string[] | undefined
}

/**
 * Patient interface 
 * @param patientId - The unique identifier for the patient
 * @param displayId - The display identifier for the patient
 * @param status - The active status of the patient
 * @param created - The creation date of the patient
 * @param meetingIds - The IDs of meetings associated with the patient
 * @param meetingData - The meeting data associated with the patient
 * @param lastMeetingId - The ID of the last meeting of the patient
 * @param nextMeetingId - The ID of the next meeting of the patient
 * @param clientIds - The IDs of clients the patient is associated with
 */
export interface Patient {
  patientId: string
  displayId: string
  status: ActiveStatus
  created: Date
  meetingIds: string[]
  meetingData: PatientMeetingData[]
  lastMeetingId?: string | undefined
  nextMeetingId?: string | undefined
  clientIds?: string[] | undefined
}

/**
 * Patient summary interface (version of patient with less data for list requests)
 * @param patientId - The unique identifier for the patient
 * @param displayId - The display identifier for the patient
 * @param status - The active status of the patient
 * @param created - The creation date of the patient
 * @param lastMeeting - The data of the last meeting of the patient
 * @param nextMeeting - The data of the next meeting of the patient
 */
export interface PatientSummary {
  patientId: string
  displayId: string
  status: ActiveStatus
  created: Date
  lastMeeting?: PatientMeetingData | undefined
  nextMeeting?: PatientMeetingData | undefined
}

/**
 * Patient meeting data interface
 * @param meetingId - The unique identifier for the meeting
 * @param creatorId - The ID of the user who created the meeting
 * @param startTime - The start time of the meeting
 * @param meetingType - The type/status of the meeting
 */
export interface PatientMeetingData {
  meetingId: string
  creatorId: string
  startTime: Date | undefined
  meetingType: MeetingStatus
}

/**
 * Calendar slot interface (a simplified view of a meeting for calendar display)
 * @param meetingId - The unique identifier for the meeting
 * @param creatorId - The ID of the user who created the meeting
 * @param startTime - The start time of the meeting
 * @param endTime - The end time of the meeting
 * @param meetingType - The type/status of the meeting
 * @param patientId - The ID of the patient associated with the meeting
 * @param attendeeIds - The IDs of attendees of the meeting
 * @param organisationIds - The IDs of organisations associated with the meeting
 */
export interface CalendarSlot {
  meetingId: string
  creatorId: string
  startTime: Date
  endTime: Date
  meetingType: MeetingStatus
  patientId: string
  attendeeIds: string[]
  organisationIds?: string[] | undefined
}

/**
 * Timeslot interface simplest representation of a time period with a start and end time
 * @param startTime - The start time of the timeslot
 * @param endTime - The end time of the timeslot
 */
export interface Timeslot {
  startTime: Date;
  endTime: Date;
}

/**
 * Conflict timeslot interface (extends Timeslot)
 * @param conflictingUsers - The IDs of users who have conflicts during this timeslot
 * @param patientConflict - Indicates if there is a patient conflict during this timeslot
 */
export interface ConflictTimeslot extends Timeslot {
  conflictingUsers: string[];
  patientConflict: boolean,
}

/**
 * Proposed timeslot interface (extends Timeslot)
 * @param creatorId - The ID of the user who proposed the timeslot
 */
export interface ProposedTimeSlot extends Timeslot {
  creatorId: string
}

/**
 * Meeting interface - Full meeting details instead of a summary as in CalendarSlot
 * @param meetingId - The unique identifier for the meeting
 * @param creatorId - The ID of the user who created the meeting
 * @param created - The creation date of the meeting
 * @param meetingType - The type/status of the meeting
 * @param realStartTime - The actual start time of the meeting
 * @param realEndTime - The actual end time of the meeting
 * @param startTime - The scheduled start time of the meeting
 * @param endTime - The scheduled end time of the meeting
 * @param proposedTimes - The proposed times for the meeting
 * @param notifyTimeAttendees - The IDs of attendees to notify about the meeting time
 * @param attendeeIds - The IDs of attendees of the meeting
 * @param joinedInfo - The join information of attendees
 * @param organisationIds - The IDs of organisations associated with the meeting
 * @param patientId - The ID of the patient associated with the meeting
 * @param survey - The survey responses for the meeting
 * @param clientIds - The IDs of clients associated with the meeting
 */
export interface Meeting {
  meetingId: string
  creatorId: string
  created?: Date | undefined
  meetingType: MeetingStatus
  realStartTime?: Date | undefined
  realEndTime?: Date | undefined
  startTime: Date
  endTime: Date
  proposedTimes?: ProposedTimeSlot[] | undefined
  notifyTimeAttendees?: string[] | undefined
  attendeeIds: string[]
  joinedInfo: MeetingJoinInfo[]
  organisationIds: string[]
  patientId: string
  survey?: MeetingSurvey[] | undefined
  clientIds?: string[] | undefined
}

/**
 * Meeting join information interface 
 * Used to check meeting status when joining with email link
 * @param userId - The ID of the user
 * @param inMeeting - Indicates if the user is currently in the meeting
 * @param joinNumber - The number of times the user has joined the meeting
 * @param acceptedInvite - Indicates if the user accepted the meeting invite
 * @param joinTime - The time the user joined the meeting
 * @param fullName - The full name of the user
 * @param jobTitle - The job title of the user
 */
export interface MeetingJoinInfo {
  userId: string
  inMeeting: boolean
  joinNumber: number
  acceptedInvite?: boolean
  joinTime?: Date
  fullName?: string
  jobTitle?: string
}

/**
 * Meeting survey interface
 * @param userId - The ID of the user who completed the survey
 * @param responses - The responses to the survey questions
 */
export interface MeetingSurvey {
  userId: string
  responses: MeetingQuestion[]
}

/**
 * Meeting question interface
 * @param questionId - The ID of the survey question
 * @param surveyResponse - The response to the survey question
 * @param testResponse - An optional test response for the survey question
 */
export interface MeetingQuestion {
  questionId: string
  surveyResponse: MeetingSurveyResponse
  testResponse?: string | undefined
}

/**
 * Meeting survey response enum
 * Represents the possible responses to a survey question
 */
export enum MeetingSurveyResponse {
  StronglyDisagree = "Strongly Disagree",
  Disagree = "Disagree",
  SomeWhatDisagree = "Somewhat Disagree",
  Neutral = "Neutral",
  SomeWhatAgree = "Somewhat Agree",
  Agree = "Agree",
  StronglyAgree = "Strongly Agree",
}

/**
 * Meeting event type enum
 * Represents the different types of events that can occur in a meeting
 */
export enum MeetingEventType {
  MeetingEnded = "ME",
  MeetingStarted = "MS",
  AttendeeJoined = "AJ",
  AttendeeLeft = "AL",
  AttendeeDropped = "AD",
  MeetingCreated = "MC",
  MeetingTime = "TI",
  MeetingCancelled = "MN",
  Upload = "UP",
  OrganisationAdded = "OA",
  OrganisationRemoved = "OR",
  AttendeeAdded = "AA",
  AttendeeRemoved = "AR",
  ScheduledStart = "SS",
  ScheduledEnd = "SE",
  SubstituteAdded = "SA",
  SubstituteFailed = "SF",
  SubstituteLink = "SL",
  ConfirmAttendance = "CA",
  ProposedTime = "PT",
  Unknown = "UN",
}

/**
 * Meeting event interface
 * @param type - The type of the meeting event
 * @param eventDate - The date and time of the event
 * @param userId - The ID of the user associated with the event (optional)
 * @param joinType - The type of join for the event (optional)
 * @param id - The ID of the event (optional)
 * @param dateField - An additional date field for the event (optional)
 */
export interface MeetingEvent {
  type: MeetingEventType,
  eventDate: Date,
  userId?: string | undefined,
  joinType?: MeetingJoinType | undefined,
  id?: string | undefined,
  dateField?: Date | undefined,
}

/**
 * User join information interface
 * @param fullName - The full name of the user
 * @param jobTitle - The job title of the user
 * @param userType - The type of the user
 * @param joinNumber - The number of times the user has joined the meeting
 * @param joinInfo - The join information for the user
 * @param meetingViews - The views of the meeting by the user
 * @param survey - The survey completed by the user (optional)
 */
export interface UserJoinInformation {
  fullName: string,
  jobTitle: string,
  userType: UserType,
  joinNumber: number,
  joinInfo: MeetingJoinInfo[];
  meetingViews: MeetingView[];
  survey?: MeetingSurvey;
}

/**
 * Meeting URL information interface
 * @param photoUrls - The URLs of the photos related to the meeting
 * @param modelUrls - The URLs of the models related to the meeting
 * @param patientDisplayId - The display ID of the patient
 */
export interface MeetingUrlInformation {
  photoUrls: string[],
  modelUrls: string[],
  patientDisplayId: string,
}

/**
 * Substitute result interface - returned result of a substitution attempt
 * @param userId - The ID of the user attempting to substitute
 * @param email - The email of the user attempting to substitute
 * @param userType - The type of the user attempting to substitute
 * @param status - The status of the substitution attempt
 * @param message - The message associated with the substitution attempt
 */
export interface SubstituteResult {
  userId: string
  email: string
  userType: UserType
  status: number
  message: string
}

/**
 * Meeting confirmation information interface
 * Used to check status of a meeting when trying to confirm or reject attendance
 * @param meetingType - The type of the meeting
 * @param startTime - The start time of the meeting
 * @param endTime - The end time of the meeting
 * @param acceptedInvite - Whether the invite was accepted (optional)
 * @param message - The message associated with the meeting confirmation
 */
export interface MeetingConfirmationInfo {
  meetingType: MeetingStatus
  startTime: Date
  endTime: Date
  acceptedInvite: boolean | undefined
  message: string
}

/**
 * Join code status enum
 * Represents the different statuses of a join code for a meeting
 */
export enum JoinCodeStatus {
  usingAuth = "usingAuth",
  valid = "valid",
  invalid = "invalid",
  missing = "missing",
  canRequestNewCode = "canRequest",
  limitReached = "limitReached",
  cannotJoinMeeting = "cannotJoin",
  waitForJoinCode = "wait",
}

/**
 * Can substitute data interface
 * Represents the data required to determine if a user can substitute for a meeting
 * @param meetingType - The type of the meeting
 * @param domainWhiteList - The list of allowed domains (optional)
 * @param message - The message associated with the substitution check
 * @param joinCodeStatus - The status of the join code for the meeting
 */
export interface CanSubstituteData {
  meetingType: MeetingStatus
  domainWhiteList?: string[]
  message: string,
  joinCodeStatus: JoinCodeStatus,
}